export const countryCodeToName = {
  DZ: 'Algeria',
  AO: 'Angola',
  BJ: 'Benin',
  BW: 'Botswana',
  BF: 'Burkina Faso',
  BI: 'Burundi',
  CM: 'Cameroon',
  CV: 'Cape Verde',
  CF: 'Central African Republic',
  TD: 'Chad',
  KM: 'Comoros',
  CG: 'Republic of the Congo',
  CD: 'Democratic Republic of the Congo',
  DJ: 'Djibouti',
  EG: 'Egypt',
  GQ: 'Equatorial Guinea',
  ER: 'Eritrea',
  SZ: 'Eswatini',
  ET: 'Ethiopia',
  GA: 'Gabon',
  GM: 'Gambia',
  GH: 'Ghana',
  GN: 'Guinea',
  GW: 'Guinea-Bissau',
  CI: 'Ivory Coast',
  KE: 'Kenya',
  LS: 'Lesotho',
  LR: 'Liberia',
  LY: 'Libya',
  MG: 'Madagascar',
  MW: 'Malawi',
  ML: 'Mali',
  MR: 'Mauritania',
  MU: 'Mauritius',
  MA: 'Morocco',
  MZ: 'Mozambique',
  NA: 'Namibia',
  NE: 'Niger',
  NG: 'Nigeria',
  RW: 'Rwanda',
  ST: 'São Tomé and Príncipe',
  SN: 'Senegal',
  SC: 'Seychelles',
  SL: 'Sierra Leone',
  SO: 'Somalia',
  ZA: 'South Africa',
  SS: 'South Sudan',
  SD: 'Sudan',
  TZ: 'Tanzania',
  TG: 'Togo',
  TN: 'Tunisia',
  UG: 'Uganda',
  ZM: 'Zambia',
  ZW: 'Zimbabwe'
};

export function getCountryName(countryCode) {
  if (!countryCode) return null;
  const str = String(countryCode).trim();

  // If already a full country name (longer than 2 characters), return as-is
  if (str.length > 2) {
    return str;
  }

  const code = str.toUpperCase();

  // Try hardcoded African countries first
  if (countryCodeToName[code]) {
    return countryCodeToName[code];
  }

  // For international countries (2-letter codes), use Intl.DisplayNames API
  if (str.length === 2) {
    try {
      if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
        const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
        const name = displayNames.of(code);
        if (name) return name;
      }
    } catch (e) {
      // Intl.DisplayNames not available or invalid code
    }
  }

  // Fallback: return the code itself
  return code;
}

/**
 * Normalizes country value (code or name) to ISO 2-letter code
 * Supports both African and international countries via provided country lists
 * @param {string} countryValue - Country code or name to normalize
 * @param {Array} countryLists - Array of country list arrays to search (e.g., [internationalCountries, africanCountries])
 * @returns {string} - ISO 2-letter country code, or empty string if not found
 */
export function normalizeCountryValueToCode(countryValue, countryLists = []) {
  if (!countryValue) return '';
  const str = String(countryValue).trim();
  if (!str) return '';

  // If already a 2-letter code, return as-is (uppercase)
  const upper = str.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) {
    return upper;
  }

  // Try to find matching country in provided lists by name
  for (const countryList of countryLists) {
    if (!Array.isArray(countryList)) continue;
    const matched = countryList.find(
      c => c && c.label && c.label.toLowerCase() === str.toLowerCase()
    );
    if (matched) return matched.value || '';
  }

  // Try hardcoded African countries map
  const codeFromMap = Object.entries(countryCodeToName).find(
    ([code, name]) => name.toLowerCase() === str.toLowerCase()
  );
  if (codeFromMap) return codeFromMap[0];

  // Try Intl.DisplayNames API (more comprehensive)
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
      const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
      // Try all possible 2-letter codes
      for (let i = 65; i <= 90; i++) {
        for (let j = 65; j <= 90; j++) {
          const testCode = String.fromCharCode(i) + String.fromCharCode(j);
          try {
            const name = displayNames.of(testCode);
            if (name && name.toLowerCase() === str.toLowerCase()) {
              return testCode;
            }
          } catch (e) {
            // Invalid code, continue
          }
        }
      }
    }
  } catch (e) {
    // Intl.DisplayNames not available
  }

  // Fallback: return empty string
  return '';
}
